/**
 * GraphQL POC Routes
 *
 * Feature-flagged routes for each GraphQL client library.
 * Each route is independently controlled by its own feature flag.
 * Libraries are lazy-loaded to separate them into individual chunks.
 */

import React, { Suspense, lazy } from 'react';
import { Route, Switch, Redirect, useRouteMatch } from 'react-router-dom';

import { useFeatureFlags } from 'components/providers/FeatureFlagProvider';
import LoadingSpinner from 'components/common/LoadingSpinner';

import GraphQLPocIndex from './GraphQLPocIndex';
import DevGraphQLLinks from '../home/DevGraphQLLinks';

// Lazy-load each GraphQL client implementation into separate chunks
const GraphQLPocFetch = lazy(
  () => import(/* webpackChunkName: "graphql-fetch" */ './GraphQLPocFetch'),
);
const GraphQLPocGraphQLRequest = lazy(
  () => import(/* webpackChunkName: "graphql-graphql-request" */ './GraphQLPocGraphQLRequest'),
);
const GraphQLPocUrql = lazy(
  () => import(/* webpackChunkName: "graphql-urql" */ './GraphQLPocUrql'),
);
const GraphQLPocApollo = lazy(
  () => import(/* webpackChunkName: "graphql-apollo" */ './GraphQLPocApollo'),
);

/**
 * Loading fallback for lazy-loaded components.
 */
const LoadingFallback: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <LoadingSpinner />
  </div>
);

/**
 * GraphQL POC routes with per-library feature flags.
 * Each library is code-split into its own bundle.
 */
const GraphQLPocRoutes: React.FC = () => {
  const { path } = useRouteMatch();
  const { features, isInitialized } = useFeatureFlags();

  if (!isInitialized) {
    return <LoadingFallback />;
  }

  // Check if any GraphQL feature is enabled
  const anyEnabled =
    features.graphqlFetch ||
    features.graphqlGraphqlRequest ||
    features.graphqlUrql ||
    features.graphqlApollo;

  if (!anyEnabled) {
    return <Redirect to="/" />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <DevGraphQLLinks />
      <Switch>
        <Route path={`${path}/fetch`}>
          {features.graphqlFetch ? <GraphQLPocFetch /> : <Redirect to={path} />}
        </Route>
        <Route path={`${path}/graphql-request`}>
          {features.graphqlGraphqlRequest ? <GraphQLPocGraphQLRequest /> : <Redirect to={path} />}
        </Route>
        <Route path={`${path}/urql`}>
          {features.graphqlUrql ? <GraphQLPocUrql /> : <Redirect to={path} />}
        </Route>
        <Route path={`${path}/apollo`}>
          {features.graphqlApollo ? <GraphQLPocApollo /> : <Redirect to={path} />}
        </Route>
        <Route path={path} exact>
          <GraphQLPocIndex enabledFeatures={features} />
        </Route>
      </Switch>
    </Suspense>
  );
};

export default GraphQLPocRoutes;
